import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/_api/join-applications',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/join-applications',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/join-applications',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/join-applications',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/join-applications',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/join-applications',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/online-programs/participants/v3/join-applications',
        destPath: '/v3/join-applications',
      },
      {
        srcPath: '/online-programs/participants/v3/bulk/join-applications',
        destPath: '/v3/bulk/join-applications',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_online-programs_join-applications';

/**
 * Creates a JoinApplication for the given program.
 *
 * Members creating their own applications cannot specify `member_id` (it is automatically set to the caller).
 * Site owners with `manage_applications` permission must provide `member_id` when creating applications on behalf of members.
 *
 * For member-initiated applications, `status` is automatically set to `APPROVAL_PENDING` (or `APPROVED` if no approval is required).
 * Site owners can specify `INVITED` or `APPROVED` status when creating applications.
 */
export function createJoinApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __createJoinApplication({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'joinApplication.createdDate' },
          { path: 'joinApplication.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.CreateJoinApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        { protoPath: '/v3/join-applications', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createJoinApplication;
}

/**
 * Retrieves a JoinApplication.
 *
 * Members can retrieve their own join applications. Site owners with appropriate permissions can retrieve any join application.
 */
export function getJoinApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __getJoinApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'GET' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.GetJoinApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/{joinApplicationId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getJoinApplication;
}

/**
 * Deletes a JoinApplication.
 *
 * Deleting a JoinApplication permanently removes them from the JoinApplication List.
 */
export function deleteJoinApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteJoinApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'DELETE' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.DeleteJoinApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/{joinApplicationId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteJoinApplication;
}

/**
 * Retrieves a list of up to 1,000 join applications, given the provided paging, filtering, and sorting.
 *
 * Members can retrieve their own join applications. Site owners with appropriate permissions can retrieve applications across all members.
 *
 * Query Join Applications runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 *
 * To learn about working with query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 */
export function queryJoinApplications(
  payload: object
): RequestOptionsFactory<any> {
  function __queryJoinApplications({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'GET' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.QueryJoinApplications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        { protoPath: '/v3/join-applications/query', data: payload, host }
      ),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplications.createdDate' },
              { path: 'joinApplications.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
            { protoPath: '/v3/join-applications/query', data: payload, host }
          ),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryJoinApplications;
}

/**
 * Searches join applications with text-based filtering across member information (email, name, nickname) and supports aggregation.
 *
 * Returns matching applications with cursor-based pagination.
 */
export function searchJoinApplications(
  payload: object
): RequestOptionsFactory<any> {
  function __searchJoinApplications({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'GET' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.SearchJoinApplications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/search',
          data: serializedData,
          host,
        }
      ),
      params: toURLSearchParams(serializedData, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplications.createdDate' },
              { path: 'joinApplications.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
            {
              protoPath: '/v3/join-applications/search',
              data: serializedData,
              host,
            }
          ),
          data: serializedData,
        },
      ],
    };

    return metadata;
  }

  return __searchJoinApplications;
}

/** Create multiple JoinApplications in a single request. Works synchronously. */
export function bulkCreateJoinApplications(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateJoinApplications({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'joinApplications.createdDate' },
          { path: 'joinApplications.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkCreateJoinApplications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/bulk/join-applications/create',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateJoinApplications;
}

/** Delete multiple JoinApplications in a single request. Works synchronously. */
export function bulkDeleteJoinApplications(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteJoinApplications({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkDeleteJoinApplications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        { protoPath: '/v3/bulk/join-applications/delete', data: payload, host }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteJoinApplications;
}

/**
 * Returns the caller member's JoinApplication for a given program.
 *
 * Uses implicit member identity from call scope. Returns NOT_FOUND if none exists.
 */
export function getCurrentJoinApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __getCurrentJoinApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'GET' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.GetCurrentJoinApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/current/{programId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCurrentJoinApplication;
}

/**
 * Deprecated. Use [GetCurrentJoinApplication](https://dev.wix.com/docs/api-reference/business-management/online-programs/join-applications/get-current-join-application).
 *
 * Returns the caller member's JoinApplication for a given program.
 *
 * Uses implicit member identity from call scope. Returns NOT_FOUND if none exists.
 * @deprecated It has been replaced with wix.onlineprograms.joinapplications.v3.JoinApplicationsService.GetCurrentJoinApplication().
 */
export function myJoinApplication(payload: object): RequestOptionsFactory<any> {
  function __myJoinApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'GET' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.MyJoinApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/my/{programId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __myJoinApplication;
}

/**
 * Approves a JoinApplication.
 *
 * Updates the status from `APPROVAL_PENDING` to `APPROVED`.
 * Returns an error if the JoinApplication is not in `APPROVAL_PENDING` status.
 *
 * After approval, if the program requires payment, the join application's `payment_status` will be updated accordingly
 * and members will need to complete payment before becoming participants.
 * For free programs, a participant is created immediately.
 */
export function approveJoinApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __approveJoinApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.ApproveJoinApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/{joinApplicationId}/approve',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __approveJoinApplication;
}

/**
 * Declines a JoinApplication.
 *
 * Updates the status from APPROVAL_PENDING to DECLINED.
 * Returns an error if the JoinApplication is not in APPROVAL_PENDING status.
 */
export function declineJoinApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __declineJoinApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.DeclineJoinApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/{joinApplicationId}/decline',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __declineJoinApplication;
}

/**
 * Cancels a JoinApplication.
 *
 * Updates the status from `APPROVAL_PENDING` to `CANCELLED`.
 * Can only cancel applications in `APPROVAL_PENDING` status. If payment was initiated, it will be cancelled.
 * Applications in other statuses cannot be cancelled.
 */
export function cancelJoinApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __cancelJoinApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.CancelJoinApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/{joinApplicationId}/cancel',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelJoinApplication;
}

/**
 * Accepts an invitation to join a program.
 *
 * Uses implicit member identity from call scope to verify ownership of the JoinApplication,
 * or `manage_applications` permission for administrative flows, and transitions its status
 * from `INVITED` to `APPROVED`.
 *
 * After accepting the invitation, if the program requires payment, call `PreparePayment` to initiate the payment flow.
 * For free programs, a participant is created automatically and the `participant_id` will be populated.
 *
 * Returns an error if:
 * - The JoinApplication does not exist or the caller does not own it and lacks manage permission
 * - The JoinApplication is not in `INVITED` status
 */
export function acceptInvite(payload: object): RequestOptionsFactory<any> {
  function __acceptInvite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.AcceptInvite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/{joinApplicationId}/accept-invite',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __acceptInvite;
}

/**
 * Updates tags for multiple JoinApplications by IDs.
 *
 * Allows assigning and/or unassigning tags in a single operation.
 * At least one of assign_tags or unassign_tags must be provided.
 */
export function bulkUpdateJoinApplicationTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateJoinApplicationTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkUpdateJoinApplicationTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/bulk/join-applications/update-tags',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateJoinApplicationTags;
}

/**
 * Updates tags for JoinApplications matching a filter.
 *
 * Allows assigning and/or unassigning tags for all JoinApplications matching the filter.
 * At least one of `assign_tags` or `unassign_tags` must be provided.
 * This operation runs asynchronously.
 * Returns a job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
 */
export function bulkUpdateJoinApplicationTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateJoinApplicationTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkUpdateJoinApplicationTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/bulk/join-applications/update-tags-by-filter',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateJoinApplicationTagsByFilter;
}

/**
 * Prepares payment for a JoinApplication or resolves an existing Pricing Plans entitlement.
 *
 * Before creating or refreshing checkout details, checks whether the member has
 * an eligible Pricing Plans entitlement for the program. If so, returns the
 * JoinApplication with `payment_status` set to `PAYMENT_SUCCESSFUL` and the
 * eligible plan IDs in `payment_info.paid_plan_ids`. No checkout is needed,
 * even when the application was previously in `PAYMENT_PENDING`.
 *
 * If no eligible entitlement exists, the JoinApplication must have
 * `payment_status` set to `PAYMENT_PENDING`, `PAYMENT_FAILED`, or
 * `PAYMENT_CANCELED`.
 *
 * For `SINGLE_PAYMENT`, creates a Wix Payments order and updates
 * `payment_status` to `PAYMENT_IN_PROGRESS`.
 *
 * For `PAID_PLANS`, validates that pricing plans are connected and updates
 * `payment_status` to `PAYMENT_IN_PROGRESS`.
 *
 * When called for `PAYMENT_FAILED` or `PAYMENT_CANCELED`, this retries payment
 * by replacing stale payment details with newly prepared payment details.
 *
 * Inspect the returned `payment_status` before starting checkout. Direct the
 * member to checkout only when it is `PAYMENT_IN_PROGRESS`. Use `ApplyCoupon`
 * to apply discounts before completing a Wix Payments checkout.
 */
export function preparePayment(payload: object): RequestOptionsFactory<any> {
  function __preparePayment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.PreparePayment',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath:
            '/v3/join-applications/{joinApplicationId}/prepare-payment',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __preparePayment;
}

/**
 * Applies a coupon to the payment order for a JoinApplication.
 *
 * Applies a coupon code to reduce the payment amount.
 * The JoinApplication must have `payment_status` set to `PAYMENT_IN_PROGRESS`,
 * `PAYMENT_FAILED`, or `PAYMENT_CANCELED`. The latter two let a member apply
 * or change a coupon when retrying after a failed or canceled payment.
 * Returns the updated order totals with discount applied.
 * Only one coupon can be applied per order.
 */
export function applyCoupon(payload: object): RequestOptionsFactory<any> {
  function __applyCoupon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.ApplyCoupon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/{joinApplicationId}/apply-coupon',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __applyCoupon;
}

/**
 * Removes a previously applied coupon from the payment order for a JoinApplication.
 *
 * The JoinApplication must have a prepared payment order with a coupon applied,
 * and `payment_status` set to `PAYMENT_IN_PROGRESS`, `PAYMENT_FAILED`, or
 * `PAYMENT_CANCELED`. The latter two let a member clear a coupon when retrying
 * after a failed or canceled payment.
 * Returns the updated order totals after removing the coupon.
 */
export function removeCoupon(payload: object): RequestOptionsFactory<any> {
  function __removeCoupon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.RemoveCoupon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/join-applications/{joinApplicationId}/remove-coupon',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __removeCoupon;
}

/**
 * Completes payment for a JoinApplication when a 100% discount coupon is applied.
 *
 * This endpoint verifies that a coupon has been applied and covers the full payment amount (order total is zero),
 * then marks the payment as successful and redeems the coupon.
 *
 * Prerequisites:
 * - `PreparePayment` must have been called to create a payment order
 * - `ApplyCoupon` must have been called with a coupon that covers 100% of the price
 * - The JoinApplication `payment_status` must be `PAYMENT_IN_PROGRESS`,
 * `PAYMENT_FAILED`, or `PAYMENT_CANCELED`. The latter two let a member close out
 * a free-coupon retry after a failed or canceled payment without re-running
 * `PreparePayment`.
 *
 * On success, the `payment_status` is updated to `PAYMENT_SUCCESSFUL` and a participant is created.
 */
export function completeFreeCouponPayment(
  payload: object
): RequestOptionsFactory<any> {
  function __completeFreeCouponPayment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.CompleteFreeCouponPayment',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath:
            '/v3/join-applications/{joinApplicationId}/complete-free-coupon-payment',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'joinApplication.createdDate' },
              { path: 'joinApplication.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __completeFreeCouponPayment;
}

/**
 * Bulk invites site members to a program with optional exclude filter.
 *
 * Creates JoinApplications in INVITED status for all site members matching the filter.
 * This operation runs asynchronously.
 * Returns a job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
 */
export function bulkInviteMembersToProgramByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkInviteMembersToProgramByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.online_programs.participants.v3.join_application',
      method: 'POST' as any,
      methodFqn:
        'wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkInviteMembersToProgramByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixOnlineprogramsJoinapplicationsV3JoinApplicationsServiceUrl(
        {
          protoPath: '/v3/bulk/join-applications/invite-by-filter',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkInviteMembersToProgramByFilter;
}
